import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormControl, FormGroup } from '@angular/forms';
import { skip, take, takeUntil } from 'rxjs/operators';
import { CommonService } from '../../../../../../../../../shared/services/common.service';
import { Subject } from 'rxjs';
import { DailyReportService } from '../../../daily-report.service';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../../../state/app.state';
import { dailyReport, projectDetails } from '../../../../../../../core/projects.selectors';
import { ProjectsService } from '../../../../../../../core/projects.service';
import { translations } from '../../../../../../../../../shared/core/shared.selectors';
import { appDataDetails } from '../../../../../../../../../shared/core/shared.selectors';

@Component({
    selector: 'app-notes-details',
    templateUrl: './notes-details.component.html',
    styleUrls: ['./notes-details.component.scss', '../input-component-styles.scss'],
    standalone: false
})
export class NotesDetailsComponent implements OnInit, OnDestroy {
  @Input() data: any;
  @Input() options: any = {
    showActionsSection: true,
  };
  form: FormGroup = new FormGroup({
    note_type: new FormControl(''),
    description: new FormControl(''),
  });
  disableOptionMenu = false;
  translations: any;
  dailyReport: any;
  noteTypeOptions: { value: string; label: string }[] = [];
  private datePipe = new DatePipe('en-US');
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private commonService: CommonService,
    private projectsService: ProjectsService,
    private dailyReportService: DailyReportService,
    private store: Store<fromRoot.State>,
    private cdRef: ChangeDetectorRef,
  ) {
    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data: any) => {
        const lookups = data?.lookups?.note_type || [];
        this.noteTypeOptions = lookups.map((item: any) => ({
          value: item.value || item,
          label: item.value || item,
        }));
      });

    this.store.select(appDataDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data: any) => {
        if (!this.noteTypeOptions?.length) {
          const lookups = data?.lookups?.note_type || [];
          this.noteTypeOptions = lookups.map((item: any) => ({
            value: item.value || item,
            label: item.value || item,
          }));
        }
      });

    this.store.select(translations)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data: any) => {
        this.translations = data;
      });
  }

  ngOnInit() {
    if (this.options.disabled) {
      this.form.disable({ emitEvent: false });
    }

    if (this.data) {
      this.form.patchValue({
        note_type: this.data?.note_type,
        description: this.data?.description,
      }, { emitEvent: false });
    } else {
      this.disableOptionMenu = true;
      const newNote = {
        id: `new_${new Date().getTime()}`,
        note_type: '',
        description: '',
      };

      this.store.select(dailyReport)
        .subscribe((res: any) => {
          const index = res.notes?.length || 0;

          this.data = {
            index: index + 1,
            ...newNote,
          };

          this.cdRef.detectChanges();
        });
    }

    this.dailyReportService.triggerSave
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        if (this.form) {
          this.save();
        }
      });

    this.dailyReportService.triggerDelete
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        if (this.form) {
          this.delete();
        }
      });

    this.form.valueChanges
      .pipe(
        skip(1),
        take(1),
        takeUntil(this.onDestroy),
      )
      .subscribe(() => {
        this.projectsService.isAnyFormDirty.next(true);
      });

    this.store.select(dailyReport)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((report: any) => {
        this.dailyReport = { ...report };
      });
  }

  save(): void {
    const { index, ...rest } = this.data;

    const values = this.form.getRawValue();
    const payload = {
      ...rest,
      ...values,
    };

    this.dailyReportService.save.next({
      type: 'notes',
      data: [payload],
    });
  }

  delete() {
    this.dailyReportService.delete.next({
      type: 'notes',
      id: this.data.id,
    });
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '-';
    return this.datePipe.transform(dateStr, 'MMM dd hh:mm a') || '-';
  }

  ngOnDestroy() {
    this.cdRef.detach();
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
